/**
 * Training exercise / sequence durations are stored as fractional minutes
 * (e.g. 7.5 = 7m30s). Timers still convert with × 60.
 */

function clampInt(n, min, max) {
  const v = Math.trunc(Number(n));
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}

export function roundMinutesToSecond(minutes) {
  const sec = Math.round(Number(minutes) * 60);
  if (!Number.isFinite(sec) || sec <= 0) return 0;
  return sec / 60;
}

/**
 * Parse coach duration into fractional minutes.
 * Accepts: 7, "7", 7.5, "7.5", "7:30", "7m30s", "7m", "30s".
 */
export function parseDurationMinutes(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? roundMinutesToSecond(value) : 0;
  }
  const raw = String(value).trim().toLowerCase().replace(",", ".");
  if (!raw) return 0;

  const mmss = raw.match(/^(\d+)\s*:\s*(\d{1,2})$/);
  if (mmss) {
    const m = parseInt(mmss[1], 10);
    const s = parseInt(mmss[2], 10);
    if (!Number.isFinite(m) || !Number.isFinite(s) || s > 59) return 0;
    return roundMinutesToSecond(m + s / 60);
  }

  const labeled = raw.match(
    /^(?:(\d+(?:\.\d+)?)\s*m(?:in(?:utes?)?)?)?\s*(?:(\d+(?:\.\d+)?)\s*s(?:ec(?:onds?)?)?)?$/
  );
  if (labeled && (labeled[1] || labeled[2]) && !/^\d+(\.\d+)?$/.test(raw)) {
    const m = labeled[1] ? parseFloat(labeled[1]) : 0;
    const s = labeled[2] ? parseFloat(labeled[2]) : 0;
    if (!Number.isFinite(m) || !Number.isFinite(s)) return 0;
    return roundMinutesToSecond(m + s / 60);
  }

  const n = parseFloat(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return roundMinutesToSecond(n);
}

export function durationMinutesToSeconds(minutes) {
  return Math.max(0, Math.round(parseDurationMinutes(minutes) * 60));
}

export function splitDurationMinutes(minutes) {
  const totalSec = durationMinutesToSeconds(minutes);
  return {
    minutes: Math.floor(totalSec / 60),
    seconds: totalSec % 60,
  };
}

export function composeDurationMinutes(minutesPart, secondsPart) {
  const m = clampInt(minutesPart, 0, 9999);
  const s = clampInt(secondsPart, 0, 59);
  return roundMinutesToSecond(m + s / 60);
}

export function formatMinutesForStorage(minutes) {
  const rounded = roundMinutesToSecond(minutes);
  if (!(rounded > 0)) return "";
  if (Number.isInteger(rounded)) return String(rounded);
  return String(parseFloat(rounded.toFixed(4)));
}

export function formatDurationLabel(minutes) {
  const { minutes: m, seconds: s } = splitDurationMinutes(minutes);
  if (m <= 0 && s <= 0) return "";
  if (m > 0 && s > 0) return `${m}m${s}s`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export function distributeMinutesAcrossSequences(totalMinutes, sequences, { asString = true } = {}) {
  const list = Array.isArray(sequences) ? sequences : [];
  if (!list.length) return list;
  const totalSec = durationMinutesToSeconds(totalMinutes);
  if (totalSec <= 0) {
    return list.map((seq) => ({
      ...seq,
      sequence_duration: asString ? "" : 0,
    }));
  }
  const n = list.length;
  const base = Math.floor(totalSec / n);
  const rem = totalSec % n;
  const indicesByOrder = list
    .map((seq, idx) => ({ idx, order: Number(seq.sequence_order) || idx + 1 }))
    .sort((a, b) => a.order - b.order);
  const perIndex = new Array(n);
  for (let rank = 0; rank < n; rank += 1) {
    const sec = base + (rank < rem ? 1 : 0);
    const mins = formatMinutesForStorage(sec / 60);
    perIndex[indicesByOrder[rank].idx] = asString ? mins : parseDurationMinutes(mins);
  }
  return list.map((seq, idx) => ({
    ...seq,
    sequence_duration: perIndex[idx],
  }));
}
